import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Box, Typography } from "@material-ui/core";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";

import httpClient from "../../lib/httpClient";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import {
  addExportButton,
  isSingleSelectOperators,
  numberFilterOperators,
  stringFilterOperators,
  tableCellRender,
  fromStringToNumberParser,
} from "../util/tableUtils";

import { highlightRow } from "./kpiUtils";

const barColors = {
  0: "#cb6d67",
  1: "#b1cb67",
  2: "#67cb95",
  3: "#FFA500",
  4: "#6789cb",
  5: "#d5b9da",
  6: "#d28ec9",
  7: "#6d9aad",
  8: "#6b771ede",
  9: "#c5a956de",
  10: "#ccc115de",
  11: "#4bce97",
  12: "#ff6ec7",
  13: "#7ec8e3",
};

export const CompanyClickAccessKpi = () => {
  const { t } = useTranslation();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [companyClicksDataFetching, setCompanyClicksDataFetching] = useState(false);
  const [companyClicksData, setCompanyClicksData] = useState([]);
  const [companyClicksDataMonths, setCompanyClicksDataMonths] = useState([]);
  const [plotData, setPlotData] = useState([]);
  const [stackedPlotData, setStackedPlotData] = useState([]);
  const [pageColorMap, setPageColorMap] = useState([]);

  const [usersUpdateDate, setUsersUpdateDate] = useState("");

  useEffect(() => {
    setCompanyClicksDataFetching(true);
    httpClient()
      .get(`/kpi/companyClicks`)
      .then((res) => res.data)
      .then(async (data) => {
        const dataWithId = data.map((item, index) => ({ id: index, ...item }));
        setCompanyClicksData(dataWithId);
        setCompanyClicksDataFetching(false);
      });
  }, []);

  useEffect(() => {
    httpClient()
      .get(`/kpi/usersUpdateDateStamp`)
      .then((res) => res.data)
      .then(async (data) => {
        const usersUpdateDate = data.filter((item) => item.Title === "updated")[0].Value;
        setUsersUpdateDate(usersUpdateDate);
      });
  }, []);

  const transformDataForStackedBar = (data) => {
    const monthMap = data.reduce((acc, { Month, Total, page }) => {
      if (!acc[Month]) {
        acc[Month] = { Month };
      }
      acc[Month][page] = Total;
      return acc;
    }, {});

    return Object.values(monthMap);
  };

  useEffect(() => {
    if (companyClicksData.length) {
      const currCompanyClicksDataMonths = [...new Set(companyClicksData.map((item) => item.Month))];
      setCompanyClicksDataMonths(currCompanyClicksDataMonths);

      const dataWithoutTotal = companyClicksData.filter((item) => item.page !== "Total");
      const plotDataWithLabel = [];
      setStackedPlotData(transformDataForStackedBar(dataWithoutTotal));

      const uniquePages = [...new Set(dataWithoutTotal.map((item) => item.page))];
      // Sort alphabetically
      uniquePages.sort();
      const pageColorMapping = [];

      uniquePages.forEach((page, index) => {
        pageColorMapping.push({ page, color: barColors[index] });
      });

      setPageColorMap(pageColorMapping);

      currCompanyClicksDataMonths.forEach((currMonth) => {
        dataWithoutTotal
          .filter((item) => item.Month === currMonth)
          .forEach((item, index) => {
            plotDataWithLabel.push({
              monthLabel:
                index === 5 ? item.Month.substring(5, 7) : index === 3 ? item.Month.substring(0, 4) : null,
              color: pageColorMapping.find((pageColor) => item.page === pageColor.page).color,
              ...item,
            });
          });

        if (dataWithoutTotal.find((item) => item.Month === currMonth)) {
          plotDataWithLabel.push({ Total: 0, monthLabel: "" });
        }
      });

      setPlotData(plotDataWithLabel);
    }
  }, [companyClicksData]);

  const companyClickColumns = [
    { field: "page", headerName: t("kpiDashboard.page"), filterOperators: stringFilterOperators, width: 200 },
    {
      field: "Month",
      headerName: t("kpiDashboard.month"),
      type: "singleSelect",
      filterOperators: isSingleSelectOperators,
      valueOptions: companyClicksDataMonths.map((month) => ({ value: month, label: month })),
    },
    {
      field: "RMA",
      headerName: "RMA",
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
    {
      field: "GRUPPO",
      headerName: "GRUPPO",
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
    {
      field: "ITA",
      headerName: "ITA",
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
    {
      field: "Total",
      headerName: t("kpiDashboard.total"),
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
  ];

  const columns = companyClickColumns.map((column) => ({
    ...column,
    flex: 1,
    valueFormatter: (params) => tableCellRender(params.value?.toString()),
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { page, Month, Total } = payload[0].payload;

      if (Total === 0) return "";

      return (
        <div className="custom-tooltip" style={{ backgroundColor: "#fff", padding: "10px" }}>
          <p>{`${t("kpiDashboard.page")}: ${page}`}</p>
          <p>{`${t("kpiDashboard.month")}: ${Month}`}</p>
          <p>{`${t("kpiDashboard.total")}: ${Total}`}</p>
        </div>
      );
    }
    return null;
  };

  const updateMessage = `${t("policy.databaseLastUpdate")} ${usersUpdateDate} . ${t("policy.updatedDaily")} `;

  return (
    <Box mb={3}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={9}>
          <Box>
            <Typography variant="body1" gutterBottom>
              Company click access
            </Typography>
            <DataGridCystellarTable
              rows={companyClicksData}
              columns={columns}
              getRowId={(rowData) => rowData.id}
              loading={companyClicksDataFetching}
              pageSizeOptions={[10, 15, 25]}
              paginationModel={paginationModel}
              onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
              customToolbarComp={true}
              customToolbarCompMsg={updateMessage}
              customToolbarWidgets={addExportButton("Company Click Access")}
              getRowClassName={(params) => highlightRow(params, "Total")}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={11}>
          <Box p={1}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                width={600}
                height={300}
                data={plotData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="monthLabel" />
                <YAxis domain={[0, Math.max(...plotData.map((item) => item.Total))]} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />

                {plotData.map((item, index) => {
                  if (item.Total === 0) return <ReferenceLine x={index} stroke="red" key={index} />;
                  else return null;
                })}

                <Bar dataKey="Total">
                  {plotData.map((entry, index) => {
                    return <Cell key={`cell-cc-${index}`} fill={entry.color} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12} sm={11}>
          <Box p={1}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                width={600}
                height={300}
                data={stackedPlotData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="Month" />
                <YAxis />
                <Tooltip />
                <Legend />

                {pageColorMap.map(({ page, color }, index) => (
                  <Bar dataKey={page} stackId="a" fill={color} barSize={60} key={index} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
